import { ArrowUpRight, Calendar } from "lucide-react";
import { HERO_BG_URL, LOGO_FULL_URL } from "@/lib/brand";
import { useCalendlyUrl, openCalendly } from "@/lib/calendly";

export default function Hero() {
    const calendlyUrl = useCalendlyUrl();

    const scrollTo = (id) => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth" });
    };

    return (
        <section
            id="hero"
            data-testid="hero-section"
            className="relative min-h-screen flex items-center overflow-hidden luxe-radial pt-28 pb-24"
        >
            {/* Background image with overlay */}
            <div className="absolute inset-0 opacity-[0.18] pointer-events-none">
                <img
                    src={HERO_BG_URL}
                    alt=""
                    className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/60 to-black" />
            </div>

            {/* Grain */}
            <div className="absolute inset-0 grain pointer-events-none" />

            <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 lg:px-16 w-full grid lg:grid-cols-12 gap-12 items-center">
                {/* Left: copy */}
                <div className="lg:col-span-7 reveal">
                    <div className="flex items-center gap-3 mb-8">
                        <div className="gold-rule-solid w-10" />
                        <span
                            data-testid="hero-overline"
                            className="text-[11px] uppercase tracking-[0.35em] text-[#D4AF37] font-semibold"
                        >
                            Your Business Beautifully Built
                        </span>
                    </div>

                    <h1
                        data-testid="hero-headline"
                        className="font-display text-5xl sm:text-6xl lg:text-7xl xl:text-[88px] leading-[1.02] tracking-tight text-white font-light"
                    >
                        Beautifully crafted
                        <br />
                        <span className="shimmer-gold italic font-normal">
                            websites for growing businesses.
                        </span>
                    </h1>

                    <p
                        data-testid="hero-subheadline"
                        className="mt-8 max-w-2xl text-base md:text-lg font-light leading-relaxed text-[#C4C4C4]"
                    >
                        RecoveryDesk builds fast, elegant, mobile-ready websites
                        tailored to small and medium-sized businesses — combining
                        thoughtful design, modern development, and ongoing support
                        to help you turn every visitor into a customer.
                    </p>

                    <div className="mt-10 flex flex-col sm:flex-row flex-wrap gap-4">
                        <button
                            data-testid="hero-contact-btn"
                            onClick={() => scrollTo("contact")}
                            className="group inline-flex items-center justify-center gap-3 bg-[#D4AF37] text-black px-8 py-4 text-xs uppercase tracking-[0.22em] font-semibold btn-gold-hover hover:bg-[#E5C158]"
                        >
                            Contact Us
                            <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                        </button>
                        {calendlyUrl ? (
                            <button
                                data-testid="hero-book-time-btn"
                                onClick={() => openCalendly(calendlyUrl)}
                                className="group inline-flex items-center justify-center gap-3 border border-[#D4AF37] text-white bg-black/40 hover:bg-[#D4AF37] hover:text-black px-8 py-4 text-xs uppercase tracking-[0.22em] font-semibold transition-all duration-300"
                            >
                                <Calendar className="w-4 h-4" strokeWidth={1.75} />
                                Book a Time
                            </button>
                        ) : null}
                    </div>

                    <div className="mt-12 flex items-center gap-6 text-[11px] uppercase tracking-[0.22em] text-[#888]">
                        <span className="flex items-center gap-2">
                            <span className="w-1.5 h-1.5 rounded-full bg-[#D4AF37] animate-pulse" />
                            Small &amp; Medium Business Focused
                        </span>
                        <span className="hidden sm:inline">·</span>
                        <span className="hidden sm:inline">Built To Perform</span>
                    </div>
                </div>

                {/* Right: logo card */}
                <div className="lg:col-span-5 reveal" style={{ animationDelay: "150ms" }}>
                    <div className="relative aspect-square max-w-md mx-auto">
                        <div className="absolute inset-0 rounded-sm bg-gradient-to-br from-[#D4AF37]/20 via-transparent to-[#D4AF37]/10 blur-2xl" />
                        <div className="relative h-full w-full border border-[#D4AF37]/25 bg-black p-2 glow-gold">
                            <div className="absolute top-2 left-2 w-6 h-6 border-t border-l border-[#D4AF37]" />
                            <div className="absolute top-2 right-2 w-6 h-6 border-t border-r border-[#D4AF37]" />
                            <div className="absolute bottom-2 left-2 w-6 h-6 border-b border-l border-[#D4AF37]" />
                            <div className="absolute bottom-2 right-2 w-6 h-6 border-b border-r border-[#D4AF37]" />
                            <img
                                src={LOGO_FULL_URL}
                                alt="RecoveryDesk — Your Business Beautifully Built"
                                className="w-full h-full object-cover"
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* Bottom fade */}
            <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-b from-transparent to-black pointer-events-none" />
        </section>
    );
}
